import React, { useEffect, useState, Suspense, lazy } from 'react';
import './App.css';
import HomePage from './components/HomePage';
import DefaultSeo from './components/DefaultSeo';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import tokenService from './services/tokenService';
import { isAdminUser } from './config/adminEmails';

const SearchPage = lazy(() => import('./components/SearchPage'));
const AuthSuccess = lazy(() => import('./components/AuthSuccess'));
const CardSetAnalysis = lazy(() => import('./components/CardSetAnalysis'));
const NewsPage = lazy(() => import('./components/NewsPage'));
const NewsArticlePage = lazy(() => import('./components/NewsArticlePage'));
const EbayItemLookup = lazy(() => import('./pages/EbayItemLookup'));
const AdminCardDatabase = lazy(() => import('./components/AdminCardDatabase'));
const AdminCollections = lazy(() => import('./components/AdminCollections'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage'));

const routeFallbackStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '60vh',
  background: '#000',
  color: '#ffd700',
};

function RouteFallback() {
  return (
    <div style={routeFallbackStyle} aria-busy="true">
      <div>Loading...</div>
    </div>
  );
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuthStatus = async () => {
    try {
      setIsLoading(true);
      const token = tokenService.getAccessToken();
      
      if (!token) {
        setUser(null);
        setIsLoggedIn(false);
        return;
      }

      // Check if token is expired and refresh if needed
      if (tokenService.isTokenExpired(token)) {
        try {
          await tokenService.refreshToken();
        } catch (error) {
          console.error('Token refresh failed:', error);
          tokenService.clearTokens();
          setUser(null);
          setIsLoggedIn(false);
          return;
        }
      }

      // Validate token with backend
      const validation = await tokenService.validateToken();
      if (validation.valid) {
        setUser(validation.user);
        setIsLoggedIn(true);
      } else {
        console.error('Token validation failed:', validation.error);
        tokenService.clearTokens();
        setUser(null);
        setIsLoggedIn(false);
      }
    } catch (error) {
      console.error('Auth status check failed:', error);
      tokenService.clearTokens();
      setUser(null);
      setIsLoggedIn(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const handleLogin = () => {
    window.location.href = 'https://web-production-9efa.up.railway.app/api/auth/google';
  };

  const handleLogout = () => {
    tokenService.clearTokens();
    setUser(null);
    setIsLoggedIn(false);
    window.location.reload();
  };

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#000',
        color: '#ffd700'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        {/* Site-wide SEO baseline; route-level Helmet blocks override it */}
        <DefaultSeo />
        {/* Global Header */}
        <header className="global-header responsive-header">
          {/* First row: Scorecard title only, centered and larger */}
          <div className="header-row header-row-top" style={{ justifyContent: 'center' }}>
            <span className="header-title main-title">Scorecard <span style={{ fontSize: "0.45em", fontWeight: 500 }}>by Man Cave Sports Cards LLC</span></span>
          </div>
          {/* Second row: navigation, social left, login/logout right */}
          <div className="header-row header-row-bottom" style={{ justifyContent: 'space-between' }}>
            <div className="header-nav-group">
              <a href="/" className="header-nav-link">Home</a>
              <a href="/search" className="header-nav-link">Recent Auction Sales</a>
              <a href="/card-set-analysis" className="header-nav-link">Card Set Analysis</a>
              <a href="/news" className="header-nav-link">Releases &amp; Guides</a>
              <a href="/ebay-bidding" className="header-nav-link">eBay Item Lookup</a>
              {/* Admin-only links */}
              {isLoggedIn && isAdminUser() && (
                <>
                  <a href="/admin/cards" className="header-nav-link admin-link">🎯 Card Database</a>
                  <a href="/admin/collections" className="header-nav-link admin-link">📂 Collections</a>
                </>
              )}
            </div>
            <div className="header-social-group">
              <a href="https://x.com/Mancavesportsc1" target="_blank" rel="noopener noreferrer" className="header-social">𝕏</a>
              <a href="https://www.instagram.com/mancavesportscardllc?igsh=NWoxOHJycGdrYzZk&utm_source=qr" target="_blank" rel="noopener noreferrer" className="header-social">📷</a>
              <a href="https://www.facebook.com/profile.php?id=100062665574017" target="_blank" rel="noopener noreferrer" className="header-social">📘</a>
            </div>
            <div className="header-user-actions">
              {!isLoggedIn ? (
                <button onClick={handleLogin} className="header-login-btn">Log in</button>
              ) : (
                <span className="header-username">
                  {user?.displayName || user?.name || ((user?.given_name || user?.givenName) && (user?.family_name || user?.familyName) ? `${user.given_name || user.givenName} ${user.family_name || user.familyName}` : '')}
                </span>
              )}
              {isLoggedIn && (
                <button onClick={handleLogout} className="header-login-btn">Log out</button>
              )}
            </div>
          </div>
        </header>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/card-set-analysis" element={<CardSetAnalysis />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/:slug" element={<NewsArticlePage />} />
            <Route path="/ebay-bidding" element={<EbayItemLookup />} />
            <Route path="/admin/cards" element={<AdminCardDatabase />} />
            <Route path="/admin/collections" element={<AdminCollections />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/auth-success" element={<AuthSuccess onAuthSuccess={checkAuthStatus} />} />
            {/* Catch-all: a useful page instead of an empty shell, marked
                noindex. Still answers HTTP 200 -- see NotFoundPage.js. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </div>
    </Router>
  );
}

export default App;
